import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import { Flex, Typography } from '@iheartjane/reefer';
import type { LabeledOption, Nullable } from '@iheartjane/types';
import { useMemo } from 'react';

import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import { MobileFiltersModal } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { BloomWizardBanner } from '@jane/bloom/components/wizardExposure/bloomWizardBanner';
import { BloomWizardButton } from '@jane/bloom/components/wizardExposure/bloomWizardButton';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { WizardOpenSource } from '@jane/bloom/services/tracking/Events';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { BrandSpecials } from '@jane/bloom/types/schemas/marketplace/brandSpecials';
import type {
  StoreSpecial,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { NavigationCategory } from '@jane/bloom/types/schemas/roots/product';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';
import type { AlgoliaProduct } from '@jane/search/types';

import styles from './categoryTab.module.css';

type DefaultCategoryTabProps = {
  brandSpecials: BrandSpecials;
  cart: Cart | undefined;
  category: NavigationCategory | undefined;
  currentSort: SortOptions;
  customCategoryLabels: Record<string, string>;
  customLanguage: CustomLanguage;
  dynamicPlacement?: SmartSort<AlgoliaProduct>;
  enabledSpecialsForRow: StoreSpecial[];
  featuredTab?: boolean;
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<AlgoliaProduct>;
  openWizard?: (source: WizardOpenSource) => void;
  pauseRefetching: () => void;
  pinnedStore?: Nullable<StoreViewModel>;
  preloadedProductIds: number[];
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  sortOptions: LabeledOption<SortOptions>[];
  specials: StoreSpecials;
  unpauseRefetching: () => void;
};

export function CategoryTab({
  brandSpecials,
  cart,
  category,
  currentSort,
  customCategoryLabels,
  customLanguage,
  dynamicPlacement,
  enabledSpecialsForRow,
  featuredTab,
  janeGold,
  menuTopRowInstance,
  openWizard,
  pauseRefetching,
  pinnedStore,
  preloadedProductIds,
  rowInstance,
  sortOptions,
  specials,
  unpauseRefetching,
}: DefaultCategoryTabProps) {
  const { featureFlags } = useRootLoaderData();
  const { skeletonLoader } = useSkeletonLoader();

  const hasResults = useMemo(
    () => (rowInstance?.products?.length ?? 0) > 0,
    [rowInstance?.products?.length]
  );

  if (!rowInstance) {
    return <></>;
  }

  if (skeletonLoader) {
    return skeletonLoader;
  }

  return (
    <main className="main">
      {!featuredTab && (
        <div className={styles['categoryTabContainer']}>
          <div className={styles['categoryTabTopRow']}>
            <MobileFiltersModal
              availableProductsCount={rowInstance.totalProducts}
              sortOptions={sortOptions}
              currentSort={currentSort}
              shouldShowGold={janeGold.shouldShowGold}
              hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
            />
            {featureFlags[FLAGS.obWizardBannerEnabled] &&
              category &&
              openWizard && (
                <div className={styles['mobileWizardButton']}>
                  <BloomWizardButton
                    onClickStart={() => openWizard(WIZARD_SOURCE.BUTTON)}
                  />
                </div>
              )}
          </div>

          {featureFlags[FLAGS.obWizardBannerEnabled] &&
            !category &&
            openWizard && (
              <div className={styles['wizardBanner']}>
                <BloomWizardBanner
                  onClickStart={() => openWizard(WIZARD_SOURCE.BANNER)}
                />
              </div>
            )}
          <Typography
            color="inherit"
            variant="header-bold"
            className="product-count"
            as="h2"
          >
            {category ? customCategoryLabels[category] : 'Shop all'}
          </Typography>
        </div>
      )}

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="500px">
          <Typography
            color="inherit"
            variant="title-bold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      {menuTopRowInstance && (
        <DMMenuRow
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={brandSpecials}
          menuTopRow={menuTopRowInstance}
          cart={cart}
          customLanguage={customLanguage}
          specials={enabledSpecialsForRow}
          janeGold={janeGold}
          pinnedStore={pinnedStore}
          embedInProductGrid={true}
        />
      )}

      {hasResults && (
        <Typography color="grays-mid" variant="body" className="product-count">
          {formatProductCount(rowInstance?.totalProducts)}
        </Typography>
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        cart={cart}
        customLanguage={customLanguage}
        dynamicPlacement={dynamicPlacement}
        key={category}
        preloadedProductIds={preloadedProductIds}
        rowInstance={rowInstance}
        specials={specials}
        janeGold={janeGold}
        pinnedStore={pinnedStore}
      />
    </main>
  );
}
